import React, { useEffect, useRef, useState } from "react";
import FuturesLeverageSlider from "./FuturesLeverageSlider";

/**
 * Right-side drawer: batch leverage + margin mode.
 * Draft (toggles + values) is owned by the parent so reopen keeps the last state.
 */
export default function FuturesBatchAdjustDrawer({
  open,
  onClose,
  maxLeverage = 125,
  initialLeverage = 20,
  initialMarginMode = "cross",
  draft,
  onDraftChange,
  onConfirm,
}) {
  const [entered, setEntered] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const wasOpenRef = useRef(false);

  const batchLeverageEnabled = !!draft?.leverageEnabled;
  const batchMarginModeEnabled = !!draft?.marginModeEnabled;
  const leverageVal =
    draft?.leverage != null && Number.isFinite(Number(draft.leverage))
      ? Number(draft.leverage)
      : initialLeverage;
  const marginModeDraft =
    draft?.marginMode === "isolated" || draft?.marginMode === "cross"
      ? draft.marginMode
      : initialMarginMode === "isolated"
        ? "isolated"
        : "cross";

  const patchDraft = (partial) => {
    onDraftChange?.({
      leverageEnabled: batchLeverageEnabled,
      marginModeEnabled: batchMarginModeEnabled,
      leverage: leverageVal,
      marginMode: marginModeDraft,
      ...partial,
    });
  };

  useEffect(() => {
    if (!open) {
      setEntered(false);
      setSubmitting(false);
      wasOpenRef.current = false;
      return undefined;
    }

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;

    // On open only: fill missing draft values from current pair settings (keep toggles).
    if (justOpened) {
      onDraftChange?.({
        leverageEnabled: batchLeverageEnabled,
        marginModeEnabled: batchMarginModeEnabled,
        leverage:
          draft?.leverage != null && Number.isFinite(Number(draft.leverage))
            ? Number(draft.leverage)
            : initialLeverage,
        marginMode:
          draft?.marginMode === "isolated" || draft?.marginMode === "cross"
            ? draft.marginMode
            : initialMarginMode === "isolated"
              ? "isolated"
              : "cross",
      });
    }

    const t = requestAnimationFrame(() => {
      requestAnimationFrame(() => setEntered(true));
    });
    return () => cancelAnimationFrame(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate only on open edge
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, submitting]);

  const clampLev = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x) || x < 1) return 1;
    return Math.min(Math.round(x), maxLeverage);
  };

  const canConfirm = (batchLeverageEnabled || batchMarginModeEnabled) && !submitting;

  if (!open) return null;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const payload = {
        leverageEnabled: batchLeverageEnabled,
        leverage: clampLev(leverageVal),
        marginModeEnabled: batchMarginModeEnabled,
        marginMode: marginModeDraft,
      };
      const ok = await onConfirm?.(payload);
      if (ok !== false) {
        // Keep toggles as-is after success so reopen still shows them On.
        patchDraft({
          leverage: payload.leverage,
          marginMode: payload.marginMode,
        });
        onClose?.();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`agcx-ft-batch-drawer-root${entered ? " agcx-ft-batch-drawer-root--open" : ""}`}
      role="presentation"
    >
      <button type="button" className="agcx-ft-batch-drawer-backdrop" aria-label="Close" onClick={onClose} />
      <aside
        className="agcx-ft-batch-drawer-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="agcx-ft-batch-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="agcx-ft-batch-drawer-head">
          <button type="button" className="agcx-ft-batch-drawer-iconbtn" onClick={onClose} aria-label="Back">
            <i className="ri-arrow-left-s-line" aria-hidden />
          </button>
          <h2 id="agcx-ft-batch-drawer-title" className="agcx-ft-batch-drawer-title">
            Batch Adjust Leverage and Margin Mode
          </h2>
          <button type="button" className="agcx-ft-batch-drawer-iconbtn" onClick={onClose} aria-label="Close">
            <i className="ri-close-line" aria-hidden />
          </button>
        </header>

        <div className="agcx-ft-batch-drawer-body">
          <section className="agcx-ft-batch-section">
            <div className="agcx-ft-batch-row">
              <div className="agcx-ft-batch-row-text">
                <span className="agcx-ft-batch-row-label">Batch adjust leverage</span>
                <p className="agcx-ft-batch-row-sub">Apply one leverage across eligible markets</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={batchLeverageEnabled}
                className={`agcx-ft-batch-switch${batchLeverageEnabled ? " is-on" : ""}`}
                onClick={() =>
                  patchDraft({
                    leverageEnabled: !batchLeverageEnabled,
                    leverage: clampLev(leverageVal),
                  })
                }
              >
                <span className="agcx-ft-batch-switch-knob" aria-hidden />
              </button>
            </div>

            {batchLeverageEnabled ? (
              <div className="agcx-ft-batch-leverage-block">
                <FuturesLeverageSlider
                  value={clampLev(leverageVal)}
                  onChange={(v) => patchDraft({ leverage: clampLev(v) })}
                  minLeverage={1}
                  maxLeverage={maxLeverage}
                />
              </div>
            ) : null}
          </section>

          <section className="agcx-ft-batch-section">
            <div className="agcx-ft-batch-row">
              <div className="agcx-ft-batch-row-text">
                <span className="agcx-ft-batch-row-label">Batch adjust margin mode</span>
                <p className="agcx-ft-batch-row-sub">Switch eligible markets to Cross or Isolated</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={batchMarginModeEnabled}
                className={`agcx-ft-batch-switch${batchMarginModeEnabled ? " is-on" : ""}`}
                onClick={() =>
                  patchDraft({
                    marginModeEnabled: !batchMarginModeEnabled,
                    marginMode: marginModeDraft,
                  })
                }
              >
                <span className="agcx-ft-batch-switch-knob" aria-hidden />
              </button>
            </div>

            {batchMarginModeEnabled ? (
              <div
                className="agcx-ft-batch-mode-segment"
                role="group"
                aria-label="Margin mode"
              >
                <span
                  className={`agcx-ft-batch-mode-segment__thumb${
                    marginModeDraft === "isolated" ? " is-isolated" : ""
                  }`}
                  aria-hidden
                />
                <button
                  type="button"
                  className={`agcx-ft-batch-mode-segment__btn${
                    marginModeDraft === "cross" ? " is-active" : ""
                  }`}
                  onClick={() => patchDraft({ marginMode: "cross" })}
                >
                  Cross
                </button>
                <button
                  type="button"
                  className={`agcx-ft-batch-mode-segment__btn${
                    marginModeDraft === "isolated" ? " is-active" : ""
                  }`}
                  onClick={() => patchDraft({ marginMode: "isolated" })}
                >
                  Isolated
                </button>
              </div>
            ) : null}
          </section>

          <div className="agcx-ft-batch-warn" role="note">
            <span className="agcx-ft-batch-warn__icon" aria-hidden>
              ⚠
            </span>
            <p>
              Leverage can update even with open positions. Margin mode only switches on markets
              with no open positions or orders. If leverage exceeds a market maximum, it is
              adjusted down automatically.
            </p>
          </div>
        </div>

        <footer className="agcx-ft-batch-drawer-foot">
          <button
            type="button"
            className="agcx-ft-batch-confirm"
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            {submitting ? "Applying…" : "Confirm"}
          </button>
        </footer>
      </aside>
    </div>
  );
}
